// Typový chip (P/M) v hlavičce okna — klikací. Po kliknutí otevře bublinu s detaily okna
// (replika legacy Magic WindowInfoBubble): titulek, identita, parametry, velikost/režim.
// Funguje pro nativní (P) i legacy (M) agendy; data sestavuje buildWindowInfo.

import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@/shared/ui/icon';

import type { DesktopWindow } from '../core/deriveDesktopProjection';
import { useLegacyWindowChromeStore } from '../legacy/legacyWindowChromeStore';

import { buildWindowInfo, type WindowInfoRow, type WindowInnerSize } from './windowInfo';

/** Šířka bubliny v px (musí odpovídat .pwm-wininfo width v index.css — kvůli clampu k okraji). */
const POPUP_W = 510;

export function WindowInfoChip({ win }: { win: DesktopWindow }) {
  const chrome = useLegacyWindowChromeStore((s) => s.byLayer[win.layerId]);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [innerSize, setInnerSize] = useState<WindowInnerSize | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const kindAttr = win.kind === 'native' ? 'native' : 'legacy';
  const label = win.kind === 'native' ? 'P' : 'M';

  const model = useMemo(
    () => buildWindowInfo(win, chrome, innerSize ?? undefined),
    [win, chrome, innerSize]
  );

  // Pozicování bubliny pod chipem (fixed, přepočet na scroll/resize) + měření vnitřního obsahu.
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const t = triggerRef.current;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.left, window.innerWidth - POPUP_W - 8));
      setPos({ top: r.bottom + 6, left });

      // Velikost = vnitřní iframe (legacy) / obsah okna (native).
      const winEl = t.closest('.pwm-win');
      const inner =
        winEl?.querySelector('iframe') ?? winEl?.querySelector('.pwm-win-content') ?? null;
      if (inner) {
        const ir = inner.getBoundingClientRect();
        setInnerSize({ w: Math.round(ir.width), h: Math.round(ir.height) });
      } else {
        setInnerSize(null);
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  // Zavření klikem mimo / Escapem.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const style: CSSProperties | undefined = pos
    ? { position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }
    : undefined;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="pwm-type-chip"
        data-kind={kindAttr}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        title="Detaily okna"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {label}
      </button>

      {open && style && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popRef}
              id={panelId}
              role="dialog"
              aria-label="Detaily okna"
              className="pwm-wininfo"
              style={style}
            >
              <div className="pwm-wininfo-head">
                <span className="pwm-type-chip" data-kind={kindAttr} aria-hidden="true">
                  {label}
                </span>
                <span className="pwm-wininfo-title" title={model.title}>
                  {model.title}
                </span>
                <button
                  type="button"
                  className="pwm-wininfo-close"
                  title="Zavřít"
                  onClick={() => setOpen(false)}
                >
                  <Icon name="x" size={13} />
                </button>
              </div>

              <div className="pwm-wininfo-body">
                <InfoRows rows={model.meta} />
                <div className="pwm-wininfo-sec">Parametry</div>
                {model.params.length > 0 ? (
                  <InfoRows rows={model.params} />
                ) : (
                  <div className="pwm-wininfo-empty">Bez parametrů</div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function InfoRows({ rows }: { rows: readonly WindowInfoRow[] }) {
  return (
    <dl className="pwm-wininfo-grid">
      {rows.map((r) => (
        <div className="pwm-wininfo-row" key={r.label}>
          <dt className="pwm-wininfo-label" title={r.label}>
            {r.label}
          </dt>
          <dd className="pwm-wininfo-val">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}
